import { ElementType } from 'react';
import LangEnum from '../../../../lang/LangEnum';
import { ColumnsPreferenceRecognizedLists } from '../../../../services/modules/userContextPreferences/userContextPreferences.types';
import { LocationState } from '../../../../types/Base.types';
import { OrderType } from '../../../Hooks/useList.types';

export interface BaseItem {
  id: string;
  parentId?: string;
}

export interface RowAction {
  label: LangEnum;
  icon: React.ReactNode;
  onClick: () => void;
  testId?: string;
}

export type GetRowActions<T> = ({ item }: { item: T }) => RowAction[];

interface VirtualizedTableHeaderActionBase {
  icon: ElementType;
  label: LangEnum;
  testId?: string;
}

interface VirtualizedTableHeaderLinkAction extends VirtualizedTableHeaderActionBase {
  to: string;
  onClick?: never;
}

interface VirtualizedTableHeaderButtonAction extends VirtualizedTableHeaderActionBase {
  onClick: () => void;
  to?: never;
}

export type VirtualizedTableHeaderAction =
  | VirtualizedTableHeaderLinkAction
  | VirtualizedTableHeaderButtonAction;

export type RenderItemProps<T> = {
  item: T;
  className?: string;
  visibleColumns: Column<T>[];
};

export type RenderItem<T> = ({
  item,
  className,
  visibleColumns,
}: RenderItemProps<T>) => React.ReactNode;

export type RenderTopItemList<T> = ({
  className,
  visibleColumns,
}: Pick<RenderItemProps<T>, 'className' | 'visibleColumns'>) => React.ReactNode;

export interface TooltipTitleProps {
  titleTooltip: LangEnum;
  tooltipIcon?: JSX.Element;
  titleTooltipParam?: { [key: string]: string };
}

export type ColumnRenderFn<T> = (
  item: T,
  itemIndex: number,
) => {
  node: React.ReactNode;
  href?: string;
  onClick?: () => void;
};

export interface Column<T> {
  title: LangEnum | string;
  tooltipTitleProps?: TooltipTitleProps;
  name: string;
  sortable?: boolean;
  defaultSort?: OrderType;
  width?: number;
  /** @default true */
  canHide?: boolean;
  hide?: boolean;
  showByDefault?: boolean;
  xs?: {
    fullWidth?: boolean;
    useAtTheTopRight?: boolean;
    showAsCardChip?: boolean;
  };
  render: ColumnRenderFn<T>;
  cardRender?: ColumnRenderFn<T>;
  renderTopColumn?: () => React.ReactNode;
  orderByName?: string;
  fixedHorizontalScroll?: boolean;
  fixedRight?: boolean;
  onClickHeaderColumn?: () => void;
}

export type InternalData<T> = T & {
  isSelected?: boolean;
  parentCheckboxState?: ParentCheckboxState;
  isParent: boolean;
  isChild: boolean;
  depth: number;
  trunkContinuations: boolean[];
  useCustomTableCell?: boolean;
  disablePointerCursor?: boolean;
};

export type ParentCheckboxState = 'checked' | 'indeterminate' | 'unchecked';

export interface CheckboxProps<T> {
  disableCheckBox: boolean;
  isAllSelected: boolean;
  onToggleSelectAll: () => void;
  onToggleSelectRow: (item: T) => void;
  onToggleSelectParent?: (parentItem: T, childItems: T[]) => void;
  selectedRows: string[];
}

export interface StickyGroupBand<T extends BaseItem> {
  row: InternalData<T>;
  position: number;
  push: number;
}

export interface StickyGroupConfig<T extends BaseItem> {
  bands: StickyGroupBand<T>[];
  bandHeight: number;
}

export interface VirtualizedTableContext {
  bandedRowIds: Set<string>;
}

export interface FirstColumnIndentParams {
  row: Pick<InternalData<BaseItem>, 'isChild' | 'depth' | 'parentId'>;
  hasRowCheckbox: boolean;
}

export interface TreeCellOffsetParams {
  depth: number;
  hasRowCheckbox: boolean;
}

export interface VirtualizedTableProps<T> {
  data: T[];
  endReached: () => void;
  getRowActions?: GetRowActions<T>;
  headerAction?: VirtualizedTableHeaderAction;
  columns: Column<T>[];
  renderParent?: RenderItem<T>;
  isFirstLoading?: boolean;
  isLoadingNextPage?: boolean;
  columnsPreferenceRecognizedList?: ColumnsPreferenceRecognizedLists;
  ExtraHeader?: React.ReactNode;
  checkboxProps?: CheckboxProps<T>;
  renderTopItemList?: RenderTopItemList<T>;
  useParentTableCell?: boolean;
  disablePointerCursor?: boolean;
  getIsDisabledRowAction?: (item: T) => boolean;
  useCardView?: boolean;
  fixedTopOffset?: number;
  customScrollParent?: HTMLElement | null;
  disableBorder?: boolean;
  restoreScrollOnMount?: boolean;
  hideColumnsButton?: boolean;
  emptyState?: React.ReactNode;
  hasActiveFilters?: boolean;
  stickyGroupHeaders?: boolean;
  isFirstLevelTreeHidden?: boolean;
}

export enum MenuType {
  HIDE_COLUMNS = 'HIDE_COLUMNS',
  ITEM = 'ITEM',
}

interface VirtualizedItemSelectionProps<T> {
  disableCheckBox?: boolean;
  onToggleSelectRow?: (item: T) => void;
  parentCheckboxState?: ParentCheckboxState;
  onToggleSelectParent?: () => void;
}

export interface VirtualizedItemTableProps<T extends BaseItem> {
  item: InternalData<T>;
  itemIndex: number;
  onClickAction?: (event: React.MouseEvent<HTMLButtonElement>, item: T) => void;
  renderParent?: RenderItem<T>;
  columns: Column<T>[];
  isLastChild: boolean;
  hasHideColumnsButton: boolean;
  selectionProps?: VirtualizedItemSelectionProps<T>;
  isFirstLoading?: boolean;
  useParentTableCell?: boolean;
  disableActionColumn?: boolean;
  hrefState?: LocationState;
  isFirstLevelTreeHidden?: boolean;
}
